import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ChangeDetectorRef, HostListener, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MenuItem, MenuConfig } from '@shared/interfaces/menu.interface';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  animations: [
    trigger('slideDown', [
      state('closed', style({
        height: '0px',
        opacity: 0,
        overflow: 'hidden',
        transform: 'translateY(-10px)'
      })),
      state('open', style({
        height: '*',
        opacity: 1,
        overflow: 'visible',
        transform: 'translateY(0)'
      })),
      transition('closed => open', animate('200ms ease-out')),
      transition('open => closed', animate('150ms ease-in'))
    ])
  ]
})
export class NavigationMenuComponent implements OnInit, OnChanges {
  @Input() items: MenuItem[] = [];
  @Input() config: MenuConfig = {
    showTitle: true,
    allowCollapse: true,
    autoCollapse: true
  };
  @Input() title: string = '';
  @Input() contextTitle: string = '';
  @Input() icon: string = '';
  @Input() basePath: string = '';

  @Output() itemClick = new EventEmitter<MenuItem>();
  @Output() itemSelected = new EventEmitter<MenuItem>();
  @Output() menuStateChange = new EventEmitter<{item: MenuItem, expanded: boolean}>();

  private destroy$ = new Subject<boolean>();
  expandedItems: Set<string> = new Set();
  activeItem: string = '';
  isDropdownOpen: boolean = false;
  isSubMenuOpen: boolean = false;
  selectedItem: MenuItem | null = null;

  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      this.updateActiveItem(event.url);
    });

    this.updateActiveItem(this.router.url);

    setTimeout(() => {
      this.setInitialSelectedItem();
    }, 0);
  }

  ngOnChanges() {
    if (this.items && this.items.length > 0) {
      this.setInitialSelectedItem();
    }
  }

  private setInitialSelectedItem() {
    if (this.items && this.items.length > 0) {
      const currentUrl = this.router.url;

      let activeParent = this.items.find(item => {
        if (item.children) {
          return item.children.some(child => {
            const childPath = child.url || child.route;
            return childPath && currentUrl.includes(childPath);
          });
        }
        return false;
      });

      if (!activeParent) {
        activeParent = this.items.find(item => {
          const targetPath = item.url || item.route;
          return targetPath && currentUrl.includes(targetPath);
        });
      }

      this.selectedItem = activeParent || this.getVisibleItems()[0] || null;

      if (this.selectedItem) {
        this.activeItem = this.selectedItem.id;
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private updateActiveItem(url: string) {
    const segments = url.split('/').filter(s => s.length > 0);
    let foundItem: MenuItem | null = null;

    for (const item of this.items) {
      if (item.children) {
        const activeChild = item.children.find(child => {
          const childPath = child.url || child.route;
          return childPath && url.includes(childPath);
        });
        if (activeChild) {
          foundItem = item;
          break;
        }
      }
    }

    if (!foundItem) {
      const item = this.items.find(item => {
        const path = item.url || item.route;
        return path && url.endsWith(path);
      });
      if (item) {
        foundItem = item;
      }
    }

    if (!foundItem) {
      for (const segment of segments) {
        const itemByUrl = this.findItemByUrl(this.items, segment);
        if (itemByUrl) {
          const parent = this.findParentOfItem(this.items, itemByUrl.id);
          foundItem = parent || itemByUrl;
          break;
        }
      }
    }

    if (foundItem) {
      this.activeItem = foundItem.id;
      this.selectedItem = foundItem;
      this.expandParentOfActiveItem(this.items, foundItem.id);
    }
    this.cdr.detectChanges();
  }

  private findParentOfItem(items: MenuItem[], childId: string): MenuItem | null {
    for (const item of items) {
      if (item.children && item.children.some(c => c.id === childId || c.route === childId || c.url === childId)) {
        return item;
      }
      if (item.children) {
        const parent = this.findParentOfItem(item.children, childId);
        if (parent) return parent;
      }
    }
    return null;
  }

  private findItemByUrl(items: MenuItem[], url: string): MenuItem | null {
    for (const item of items) {
      if (item.url === url || item.route === url) {
        return item;
      }
      if (item.children) {
        const found = this.findItemByUrl(item.children, url);
        if (found) return found;
      }
    }
    return null;
  }

  private expandParentOfActiveItem(items: MenuItem[], activeId: string, parentId?: string): boolean {
    for (const item of items) {
      if (item.id === activeId) {
        if (parentId) {
          this.expandedItems.add(parentId);
        }
        return true;
      }
      if (item.children) {
        if (this.expandParentOfActiveItem(item.children, activeId, item.id)) {
          return true;
        }
      }
    }
    return false;
  }

  onItemClick(item: MenuItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (item.disabled) {
      return;
    }

    const isSameItem = this.selectedItem?.id === item.id;

    this.selectedItem = item;

    if (item.children && item.children.length > 0) {
      if (!isSameItem) {
        this.navigateToItem(item.children[0]);
        this.closeDropdown();
      }
      this.itemClick.emit(item);
    } else {
      this.navigateToItem(item);
      this.closeDropdown();
      this.itemClick.emit(item);
    }
  }

  onChildClick(parent: MenuItem, child: MenuItem, event: Event) {
    event.stopPropagation();

    const currentUrl = this.router.url;
    const isCurrentChild = currentUrl.includes(child.url || child.route || '');

    if (isCurrentChild) {
      event.preventDefault();
      return false;
    }

    this.selectedItem = parent;
    this.navigateToItem(child);
    this.closeDropdown();
  }

  private navigateToItem(item: MenuItem) {
    if (item.url || item.route) {
      const targetRoute = item.route || item.url;

      if (!targetRoute) {
        return;
      }

      let fullPath: string;

      if (targetRoute.startsWith('/')) {
        fullPath = targetRoute;
      } else if (this.basePath) {
        fullPath = `/main-view/${this.basePath}/${targetRoute}`;
      } else {
        fullPath = `/main-view/${targetRoute}`;
      }

      this.router.navigate([fullPath]);
      this.activeItem = item.id;

      this.itemSelected.emit(item);
    }

    if (item.action) {
      item.action();
      this.itemSelected.emit(item);
    }
  }

  isExpanded(item: MenuItem): boolean {
    return this.expandedItems.has(item.id);
  }

  isActive(item: MenuItem): boolean {
    return this.activeItem === item.id;
  }

  isChildActive(item: MenuItem): boolean {
    if (!item.children) return false;
    return item.children.some(child => this.router.url.includes(child.url || child.route || ''));
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  getActiveChildLabel(item: MenuItem): string {
    if (!item.children) return '';
    const activeChild = item.children.find(child => this.router.url.includes(child.url || child.route || ''));
    return activeChild ? activeChild.label : (item.children[0]?.label || '');
  }

  getVisibleItems(): MenuItem[] {
    return this.items.filter(item => item.visible !== false);
  }

  getVisibleChildren(item: MenuItem): MenuItem[] {
    if (!item.children) return [];
    return item.children.filter(child => child.visible !== false);
  }

  trackByItemId(index: number, item: MenuItem): string {
    return item.id;
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  toggleSubMenu(isOpen: boolean): void {
    this.isSubMenuOpen = isOpen;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  getCurrentItemLabel(): string {
    if (this.selectedItem) {
      return this.selectedItem.label;
    }

    const currentUrl = this.router.url;
    const activeItem = this.items.find(item =>
      item.url && currentUrl.includes(item.url)
    );

    if (activeItem) {
      this.selectedItem = activeItem;
      return activeItem.label;
    }

    const firstVisible = this.getVisibleItems()[0];
    return firstVisible ? firstVisible.label : 'Seleccionar opción';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const clickedInsideNavigation = target.closest('.navigation-dropdown');
    const clickedInsideMatMenu = target.closest('.mat-mdc-menu-panel');

    if (!clickedInsideNavigation && !clickedInsideMatMenu) {
      this.closeDropdown();
    }

    if (!clickedInsideNavigation && !clickedInsideMatMenu && this.menuTrigger && this.menuTrigger.menuOpen) {
      this.menuTrigger.closeMenu();
    }
  }
}
